import {
  StepAccordion,
  AccordionMinimumHeight,
  ButtonColorEnum,
  AccordionBackgroundColor,
} from "../../../../Utils/StepAccordion";
import { NorlysCheckbox } from "../../../../Utils/UI/Checkbox/NorlysCheckbox";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import "./SummaryStep.scss";
import { makeOrder } from "../../../../../Models/OrderRequest";
import { submitOrder } from "../../../../../Services/ApiService";
import { useContext, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { DashedLine } from "../../../../Utils/DashedLine/DashedLine";
import TokenContext from "../../../../../Contexts/TokenContext";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import { useHistory } from "react-router-dom";
import { UrlHelper } from "../../../../../Helpers/UrlHelper";
import { PriceDetail } from "../../../../Utils/UI/PriceDetails/PriceDetail";
import { ProductSubcategory } from "../../../../../Models/Enums/ProductCategoryEnum";
import { Typography } from "@mui/material";
import { getConsentText, hasConsent } from "../../../../../Services/ConsentService";
import { useOrder } from "../../../../../Contexts/OrderContext";
import { isCompleteAddressRequest } from "../../../../../Helpers/AddressHelpers";


const SummaryStep = ({
  previousStep,
  open,
  toggleOpen,
  completeOrder,
  setOrderResult,
}: IStepInterface) => {
  const { order, updateOrder } = useOrder();
  const [loading, setLoading] = useState({
    loading: false,
    loadingTitle: "",
    loadingText: "",
  });

  const consentText = getConsentText();

  useEffect(() => {
    if (!order.groupConsent && order.groupConsentTracking) {
      updateOrder({ groupConsentTracking: false });
    }
  }, [order.groupConsent, order.groupConsentTracking, updateOrder]);

  const partnerContentContext = useContext(PartnerContentContext);
  const tokenContext = useContext(TokenContext);
  const history = useHistory();

  const groupConsentText = `	
				<div>
          ${consentText.terms.marketing}
				</div>			
				`;
  const groupConsentFooter = `${consentText.terms.footer}
   <div style="margin-top: 10px; line-height: 1.5;">
    ${consentText.terms.prefCenterFooter}  
   </div>
  `;

  const groupConsentTrackingText = `${consentText.terms.tracking}`;

  const primaryAddressComplete = isCompleteAddressRequest(order.addressRequest);

  const addressRequestsComplete = (): boolean => {
    const separateAddressComplete =
      !order.emobilitySelected ||
      !order.separateDeliveryAddress ||
      (!!order.separateDeliveryAddressId &&
        isCompleteAddressRequest(order.separateDeliveryAddressRequest));
    return primaryAddressComplete && separateAddressComplete;
  };

  const formValid = (): boolean => {
    return (
      !!order.fullName &&
      !!order.deliveryAddress &&
      !!order.deliveryAddressId &&
      addressRequestsComplete() &&
      !!order.cpr &&
      !!order.phone &&
      !!order.email &&
      order.acceptedTerms &&
      tokenContext.length > 0 &&
      order.IDQValid &&
      (order.DatahubValid ||
        order.startDate !== undefined ||
        order.CohabDatahubValid)
    );
  };

  const showHiddenCprNumber = (cpr: string): string => {
    if (cpr) {
      let result = `${cpr.substring(0, 6)}-xxxx`;
      return result;
    }
    return "";
  };

  const handleSubmitOrder = async () => {
    if (loading.loading) return;
    // Guard against submitting when required fields (e.g. address) are missing,
    // in case the submit button is ever triggered while it should be disabled.
    if (!formValid()) return;
    setLoading({
      ...loading,
      loading: true,
      loadingText: "Din ordre behandles",
      loadingTitle: "Vent venligst...",
    });

    let orderDto = makeOrder(
      order,
      partnerContentContext.partnerId,
      partnerContentContext.estimatedYearlyUsage,
    );

    let result = await submitOrder(tokenContext, orderDto);

    if (result.isSuccessful) {
      if (completeOrder) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "eec.SaleCompleted",
          eventCategory: "Ecommerce",
          eventAction: "Transactions",
          eventLabel: "Order_Partner_" + partnerContentContext.partnerId,
        });

        if (result.data) {
          if (setOrderResult) {
            setOrderResult(result.data);
          }
        }
        completeOrder();
      }
    } else {
      history.push(`/${partnerContentContext.partnerId}/faq`);
    }

    setLoading({
      ...loading,
      loading: false,
      loadingText: "",
      loadingTitle: "",
    });
  };

  function isEastInstallation(): boolean {
    return parseInt(order.addressRequest.postCode) < 5000;
  }

  function getTermsInfoText(termsInfoFromConfig: string): string {
    let termsInfo = "";

    const electricityTerms = `<a href="https://norlysdk-prod-endpoint-media.azureedge.net/media/2ljiq4t1/leveringsbetingelser-el-privat-010725.pdf" rel="noreferrer" target="_blank">Elaftalen</a>`;
    const emobilityOwnershipTerms = `<a href="https://norlys.dk/media/wjpdu43w/da-emobility-privat-leveringsbetingelser-ejer-nov2023.pdf" rel="noreferrer" target="_blank">Ladeboks eje aftalen</a>`;
    const emobilityRentalTerms = `<a href="https://norlys.dk/media/mzxjs2ct/da-emobility-privat-leveringsbetingelser-leje-nov2023.pdf" rel="noreferrer" target="_blank">Ladeboks leje aftalen</a>`;

    if (order.chosenProducts.electricityProduct)
      termsInfo +=
        termsInfo.length > 0 ? ` og ${electricityTerms}` : electricityTerms;
    if (order.chosenProducts.chargingstandProduct) {
      if (
        order.chosenProducts.chargingstandProduct.subcategory ===
        ProductSubcategory.EJE
      )
        termsInfo +=
          termsInfo.length > 0
            ? ` og ${emobilityOwnershipTerms}`
            : emobilityOwnershipTerms;
      else
        termsInfo +=
          termsInfo.length > 0
            ? ` og ${emobilityRentalTerms}`
            : emobilityRentalTerms;
    }
    return `${termsInfo + " " + termsInfoFromConfig}`;
  }

  return (
    <StepAccordion
      loading={loading.loading}
      loadingText={loading.loadingText}
      loadingTitle={loading.loadingTitle}
      label={partnerContentContext.summaryStep.title}
      open={open}
      click={toggleOpen}
      firstButtonLabel={
        partnerContentContext.summaryStep.backButton
          ? partnerContentContext.summaryStep.backButton
          : "Tilbage"
      }
      firstButtonOutline={true}
      firstButton={previousStep}
      secondButtonLabel={
        partnerContentContext.summaryStep.submitOrderButton
          ? partnerContentContext.summaryStep.submitOrderButton
          : "Bestil ordre"
      }
      secondButtonDisabled={!formValid()}
      secondButtonType={
        formValid() ? ButtonColorEnum.DARK_GREEN : ButtonColorEnum.GREY
      }
      secondButton={handleSubmitOrder}
      minimumHeight={AccordionMinimumHeight.SMALL}
      firstButtonId="SummaryStepReturn"
      secondButtonId="SummaryStepForward"
      secondButtonShadow={true}
      backgroundColor={AccordionBackgroundColor.GREY}
    >
      <div className="summary-content">
        <div className="summary-container">
          <p id="subTitle">{partnerContentContext.summaryStep.subTitle}</p>
          {!addressRequestsComplete() && (
            <p role="alert">
              Adresseoplysningerne er ikke klar. Vent et øjeblik, eller gå tilbage og vælg adressen igen.
            </p>
          )}
          {order.chosenProducts.electricityProduct && primaryAddressComplete && (
            <PriceDetail
              price={
                isEastInstallation()
                  ? order.chosenProducts.electricityProduct.mainPrice
                  : order.chosenProducts.electricityProduct.secondaryPrice
              }
              unit={order.chosenProducts.electricityProduct.unit}
              renderInfoText={false}
              header={order.chosenProducts.electricityProduct.title}
              subcategory={order.chosenProducts.electricityProduct.subcategory}
              category={order.chosenProducts.electricityProduct.category}
              discounts={{
                installationDiscount: false,
                subscriptionDiscount: false,
                substitutionDiscount: false,
              }}
              substitution={false}
              emobilityInstallationDiscount={
                order.chosenProducts.electricityProduct
                  .emobilityInstallationDiscount
              }
              emobilitySubscriptionDiscount={
                order.chosenProducts.electricityProduct
                  .emobilitySubscriptionDiscount
              }
              emobilitySubstitutionDiscount={
                order.chosenProducts.electricityProduct
                  .emobilitySubstitutionDiscount
              }
            />
          )}

          {order.chosenProducts.chargingstandProduct && (
            <>
              <DashedLine />
              <PriceDetail
                price={order.chosenProducts.chargingstandProduct.mainPrice}
                unit={order.chosenProducts.chargingstandProduct.unit}
                renderInfoText={false}
                header={order.chosenProducts.chargingstandProduct.title}
                subcategory={
                  order.chosenProducts.chargingstandProduct.subcategory
                }
                category={order.chosenProducts.chargingstandProduct.category}
                discounts={{
                  installationDiscount:
                    partnerContentContext.emobilityInstallationDiscountActive,
                  subscriptionDiscount:
                    partnerContentContext.emobilitySubscriptionDiscountActive,
                  substitutionDiscount:
                    partnerContentContext.emobilitySubstitutionDiscountActive,
                }}
                substitution={order.emobilityRemovalOfChargingStand ?? false}
                emobilityInstallationDiscount={
                  order.chosenProducts.chargingstandProduct
                    .emobilityInstallationDiscount
                }
                emobilitySubscriptionDiscount={
                  order.chosenProducts.chargingstandProduct
                    .emobilitySubscriptionDiscount
                }
                emobilitySubstitutionDiscount={
                  order.chosenProducts.chargingstandProduct
                    .emobilitySubstitutionDiscount
                }
              />
            </>
          )}

          <DashedLine marginTop="30" marginBottom="10" />
          <div className="summary-row">
            <div className="summary-column">
              <div>
                <div>For- og efternavn</div>
                <div className="bold">{order.fullName}</div>
              </div>
            </div>
            <div className="summary-column cpr-desktop">
              <div>
                <div>CPR-nummer</div>
                <div className="bold">{showHiddenCprNumber(order.cpr)}</div>
              </div>
            </div>
          </div>

          {order.cohabFullName !== "" && (
            <div className="summary-row">
              <div className="summary-column">
                <div>
                  <div>Samlevers For- og efternavn</div>
                  <div className="bold">{order.cohabFullName}</div>
                </div>
              </div>
              <div className="summary-column cpr-desktop">
                <div>
                  <div>Samlevers CPR-nummer</div>
                  <div className="bold">
                    {showHiddenCprNumber(order.cohabCpr)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="summary-row">
            <div className="summary-column">
              <div>
                <div>E-mail</div>
                <div className="bold">{order.email}</div>
              </div>
            </div>
            <div className="summary-column">
              <div>
                <div>Telefonnummer</div>
                <div className="bold">{order.phone}</div>
              </div>
            </div>
          </div>

          <div className="summary-row">
            <div className="summary-column">
              <div>
                <div>
                  {order.emobilitySelected
                    ? "Installationsadresse"
                    : "Leveringsadresse"}
                </div>
                <div className="bold">{order.deliveryAddress}</div>
              </div>
            </div>
            {order.separateDeliveryAddress && (
              <div className="summary-column">
                <div>
                  <div>Alternativ leveringsadresse</div>
                  <div className="bold">
                    {order.separateDeliveryAddressString}
                  </div>
                </div>
              </div>
            )}
          </div>
          {order.billingAddress && (
            <div className="summary-row">
              <div className="summary-column">
                <div>
                  <div>Faktureringsadresse</div>
                  <div className="bold">{order.billingAddress}</div>
                </div>
              </div>
              <div className="summary-column"></div>
            </div>
          )}

          {UrlHelper.urlContains("salesFlow") && (
            <div className="summary-row">
              <div className="summary-column">
                <div>
                  <div>Sælger ID</div>
                  <div className="bold">{order.salesPersonId}</div>
                </div>
              </div>
              <div className="summary-column">
                <div>
                  <div>Butiksnummer</div>
                  <div className="bold">{order.shopId}</div>
                </div>
              </div>
            </div>
          )}

          <div className="summary-row cpr-mobile">
            <div className="summary-column">
              <div>
                <div>CPR-nummer</div>
                <div className="bold">{showHiddenCprNumber(order.cpr)}</div>
              </div>
            </div>
          </div>

          {order.cohabCpr !== "" && (
            <div className="summary-row cpr-mobile">
              <div className="summary-column">
                <div>
                  <div>Samlevers CPR-nummer</div>
                  <div className="bold">
                    {showHiddenCprNumber(order.cohabCpr)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {order.startDate && (
            <div className="summary-row">
              <div className="summary-column">
                <div>
                  <div>Indflytningsdato</div>
                  <div className="bold">
                    {order.startDate.toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DashedLine marginTop="30" marginBottom="10" />
        <div className="privacy-policy">
          <p className="bold">Vi passer godt på dine data</p>
          <div>
            Du kan læse hvordan vi behandler dine data i vores{" "}
            <a href="https://norlys.dk/persondatapolitik" target="_blank" rel="noopener noreferrer">
              persondatapolitik
            </a>
          </div>
        </div>

        <DashedLine marginTop="30" marginBottom="20" />

        <div className="accept-terms">
          <NorlysCheckbox
            checked={order.acceptedTerms}
            onChange={(checked) => {
              updateOrder({ acceptedTerms: checked });
            }}
            labelFontSize="16px"
            htmlLabel={`Jeg har læst og accepteret betingelserne for ${getTermsInfoText(partnerContentContext.summaryStep.terms)}.`}
          />
          <div className="terms-subtext">
            {partnerContentContext.summaryStep.termsInfo}
          </div>
        </div>
       
        {!hasConsent() && (
          <>
            <DashedLine marginTop="20" />
            <div className="group-consent">
              <NorlysCheckbox
                checked={order.groupConsent}
                onChange={(checked) => {
                  updateOrder((previousOrder) => ({
                    groupConsent: checked,
                    groupConsentTracking: checked
                      ? previousOrder.groupConsentTracking
                      : false,
                  }));
                }}
                labelFontSize="16px"
                htmlLabel={groupConsentText}
              />
              <Typography>
                <>
                  <div className="group-consent tracking-consent">
                    <NorlysCheckbox
                      key={`group-tracking-${order.groupConsent}-${order.groupConsentTracking}`}
                      checked={order.groupConsent && order.groupConsentTracking}
                      disabled={!order.groupConsent}
                      onChange={(checked) => {
                        updateOrder((previousOrder) => ({
                          groupConsentTracking: previousOrder.groupConsent
                            ? checked
                            : false,
                        }));
                      }}
                      labelFontSize="16px"
                      htmlLabel={groupConsentTrackingText}
                    />
                  </div>
                </>
              </Typography>
              <Typography>
                <span
                  style={{ fontSize: "11px" }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(groupConsentFooter) }}
                ></span>
              </Typography>
            </div>
          </>
        )}

        <DashedLine marginTop="20" marginBottom="20" />
        <div className="token-container">
          <div className="g-recaptcha" id="recaptchaSummary"></div>
        </div>
      </div>
    </StepAccordion>
  );
};

export default SummaryStep;
